import { uuid } from "@xapp/chat-widget-core";
import {
    ChannelActionRequest,
    IntentRequest,
    LaunchRequest,
    OptionSelectRequest,
    PermissionRequest,
    Response
} from "stentor-models";
import { StentorApiConfig } from "../config/search-config";
import { err } from "../utils";

export type StentorRequest = IntentRequest | LaunchRequest | OptionSelectRequest | PermissionRequest | ChannelActionRequest;

const delay = (waitTimeInMs: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, waitTimeInMs);
  });

let API: StentorApi;

export class StentorApi {

    private readonly config: StentorApiConfig;
    private session: StentorApiSession | undefined;

    private constructor(config?: StentorApiConfig) {
        this.config = config || this.config;
    }

    public static getApi(config?: StentorApiConfig): StentorApi {
        if (API) {
            return API;
        }

        API = new StentorApi(config);

        return API;
    }

    public launchRequest(query: string): Promise<Response> {
        return this.send({
            type: "LAUNCH_REQUEST",
            rawQuery: query,
            intentId: "LaunchRequest",
            platform: "stentor-platform",
            channel: "intelligent-search"
        } as StentorRequest);
    }

    public intentRequest(query: string): Promise<Response> {
        return this.send({
            type: "INTENT_REQUEST",
            rawQuery: query,
            intentId: "NLU_RESULT_PLACEHOLDER",
            platform: "stentor-platform",
            channel: "intelligent-search"
        } as StentorRequest);
    }

    public channelActionRequest(data: unknown, action: string): Promise<Response> {
        return this.send({
            type: "CHANNEL_ACTION_REQUEST",
            action,
            platform: "stentor-platform",
            channel: "intelligent-search",
            attributes: {
                data
            }
        } as unknown as StentorRequest);
    }

    public resetSession(): void {
        this.session = undefined;
    }

    private async send(data: Omit<StentorRequest, "sessionId" | "userId" | "isNewSession">): Promise<Response> {
        const { url, key } = this.config;

        const newSession = !this.session;

        if (!this.session) {
            this.session = {
                sessionId: `stentor-audio-search-session-${uuid()}`,
                userId: uuid()
            };
        }

        const { sessionId, userId } = this.session;
        const request = {
            ...data,
            sessionId,
            userId,
            isNewSession: newSession
        } as StentorRequest;
        const body = JSON.stringify(request);

        const params: RequestInit = {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${key}`,
            },
            body,
            mode: "cors"
        };

        let response = await fetch(url, params);

        if (!response.ok) {
            err(`Stentor call failed: ${response.statusText}`);
            
            await delay(1000);
            response = await fetch(url, params); // once more
            if (!response.ok) {
                err(`Stentor call failed again: ${response.statusText}`);
                throw new Error(`Status ${response.status}, Text: ${response.statusText}`);
            }
        }

        return response.json();
    }

}

export interface StentorApiSession {
    readonly sessionId: string;
    readonly userId: string;
}
